import {
  Fragment,
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  ArrowDown01Icon,
  ArrowUp01Icon
} from "@hugeicons-pro/core-stroke-rounded";
import { cx } from "shared/utils";
import { Show } from "shared/components";
import type { CallPlayback } from "shared/hooks/use-call-playback";

export type TranscriptWord = {
  text: string;
  start_seconds: number;
  end_seconds: number;
};

export type TranscriptLine = {
  speaker: string;
  message: string;
  // Offset into the recording where this line starts, in seconds. Null or
  // absent means the line has no timing and cannot be synced to playback:
  // human calls transcribed before CRM-1330 are whole-leg blobs with no
  // timings, and are deliberately not backfilled. An AI agent line starts at
  // its first word once its words are aligned, else at ElevenLabs' whole-second
  // turn stamp.
  start_seconds?: number | null;
  // Where the line ends: human calls, and AI agent lines with aligned words.
  end_seconds?: number | null;
  // The line's own word timings, for seeking to a word. Both call types carry
  // them: human calls transcribed once word storage shipped, and AI agent calls
  // whose recording was re-transcribed for word timings (CRM-1561), where the
  // API aligns them onto the agent's own text so they spell the message. Null
  // when the call or line has none, in which case the line seeks as a whole.
  words?: TranscriptWord[] | null;
};

const DEFAULT_PREVIEW_LINES = 3;

// While following playback the transcript scrolls inside its own box. Scrolling
// the active line into view with scrollIntoView would instead move the nearest
// scrollable ancestor — the whole inbox thread, or the details drawer's body.
const SYNC_PANE_MAX_HEIGHT_PX = 240;
// A boxed (Inbox) transcript scrolls inside its panel so a long call doesn't
// stretch the thread.
const BOXED_PANE_MAX_HEIGHT_PX = 320;

// After the reader scrolls the pane by hand, stop yanking it back for a moment
// so they can read ahead of (or behind) the audio.
const USER_SCROLL_PAUSE_MS = 3000;

type LineTimeline = { index: number; start: number }[];
type WordTimeline = { line: number; word: number; start: number }[];
type ActiveWord = { line: number; word: number } | null;

// The words are rendered in place of the message, so they must reproduce it
// exactly — a provider whose utterance text and word list disagree (different
// punctuation, dropped fillers) falls back to the plain message and seeks by
// line, rather than showing text the user has not seen before.
const renderableWords = (line: TranscriptLine): TranscriptWord[] | null => {
  const words = line.words;
  if (!words || words.length === 0) return null;
  return words.map((word) => word.text).join(" ") === line.message
    ? words
    : null;
};

// Both scans read "the last entry that has started": between two entries the
// earlier one stays active, so a pause keeps the highlight on what was said.
const findActiveIndex = (timeline: LineTimeline, time: number | null) => {
  if (time == null) return null;

  let active: number | null = null;
  for (const entry of timeline) {
    if (entry.start > time) break;
    active = entry.index;
  }
  return active;
};

const findActiveWord = (
  timeline: WordTimeline,
  time: number | null
): ActiveWord => {
  if (time == null) return null;

  let active: ActiveWord = null;
  for (const entry of timeline) {
    if (entry.start > time) break;
    active = entry;
  }
  return active && { line: active.line, word: active.word };
};

const sameActiveWord = (a: ActiveWord, b: ActiveWord) =>
  a === b || (!!a && !!b && a.line === b.line && a.word === b.word);

type TranscriptRowProps = {
  index: number;
  speaker: string;
  isAgent: boolean;
  message: string;
  words: TranscriptWord[] | null;
  start: number | null | undefined;
  isActive: boolean;
  activeWordIndex: number | null;
  expanded: boolean;
  syncable: boolean;
  speakerClassName: string;
  messageClassName: string;
  boxed: boolean;
  onSeek: (seconds: number) => void;
  registerRow: (index: number, el: HTMLElement | null) => void;
  registerMessage: (index: number, el: HTMLSpanElement | null) => void;
};

// Memoized so that, while the recording plays, only the row whose active word
// changed re-renders — a ten-minute call is over a thousand word spans.
const TranscriptRow = memo(function TranscriptRow({
  index,
  speaker,
  isAgent,
  message,
  words,
  start,
  isActive,
  activeWordIndex,
  expanded,
  syncable,
  speakerClassName,
  messageClassName,
  boxed,
  onSeek,
  registerRow,
  registerMessage
}: TranscriptRowProps) {
  const canSeek = syncable && typeof start === "number";

  // Each row is a subgrid of the pane's two columns: it stays one element
  // (so it can be highlighted, clicked and scrolled to) while its speaker
  // cell shares the column width every other row resolved to.
  const className = cx(
    "col-span-2 grid grid-cols-subgrid",
    syncable && "w-full rounded px-1 py-0.5 text-left transition-colors",
    isActive && "bg-primary-50",
    canSeek && "cursor-pointer",
    canSeek && !isActive && "hover:bg-alpha-20"
  );

  const content = (
    <>
      <span
        className={cx(
          "flex font-medium text-content-300",
          !boxed && "min-w-[100px]",
          isActive && "text-content-1000",
          speakerClassName
        )}
        title={isAgent ? undefined : speaker}
      >
        <span className="truncate">{speaker}</span>
        {boxed ? null : ":"}
      </span>
      <span
        ref={(el) => registerMessage(index, el)}
        className={cx(
          "min-w-0",
          expanded ? "whitespace-pre-wrap break-words" : "truncate",
          messageClassName
        )}
      >
        {canSeek && words
          ? words.map((word, j) => (
              <Fragment key={j}>
                {j > 0 && " "}
                <span
                  data-start={word.start_seconds}
                  className={cx(
                    "rounded-sm",
                    j === activeWordIndex && "bg-primary-100",
                    "hover:underline"
                  )}
                >
                  {word.text}
                </span>
              </Fragment>
            ))
          : message}
      </span>
    </>
  );

  if (!canSeek)
    return (
      <div ref={(el) => registerRow(index, el)} className={className}>
        {content}
      </div>
    );

  return (
    <button
      type="button"
      ref={(el) => registerRow(index, el)}
      className={className}
      // Words are plain spans, not buttons — a button may not contain another
      // button — so the click is read here: on a word, seek to that word;
      // anywhere else on the row, to the line's start.
      onClick={(event) => {
        const target = event.target as HTMLElement;
        const wordEl = target.closest<HTMLElement>("[data-start]");
        const wordStart = wordEl ? Number(wordEl.dataset.start) : NaN;
        onSeek(Number.isFinite(wordStart) ? wordStart : start);
      }}
    >
      {content}
    </button>
  );
});

export const CallTranscript = ({
  lines,
  contactName = "Unknown",
  previewLines = DEFAULT_PREVIEW_LINES,
  hideHeader = false,
  speakerClassName = "",
  messageClassName = "",
  toggleClassName = "text-content-200 hover:text-content-400",
  boxed = false,
  playback
}: {
  lines: TranscriptLine[];
  contactName?: string;
  previewLines?: number;
  hideHeader?: boolean;
  speakerClassName?: string;
  messageClassName?: string;
  toggleClassName?: string;
  // Inbox call cards: the lines sit in a tinted panel, names without colons.
  boxed?: boolean;
  // Bus shared with the recording player above this transcript (CRM-1330).
  // Without it the transcript is plain text, exactly as before.
  playback?: CallPlayback;
}) => {
  const [expanded, setExpanded] = useState(false);
  // A collapsed transcript clamps every visible line to a single line, so a
  // long line is cut even when the transcript is short (<= previewLines). Track
  // whether any visible line is actually clipped so the expander still appears
  // and the full text is always reachable — not only when there are extra rows.
  const [hasClippedLine, setHasClippedLine] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [activeWord, setActiveWord] = useState<ActiveWord>(null);
  const messageRefs = useRef<(HTMLSpanElement | null)[]>([]);
  const rowRefs = useRef<(HTMLElement | null)[]>([]);
  const paneRef = useRef<HTMLDivElement>(null);
  // Set when the reader collapses the transcript themselves, so playback does
  // not immediately re-open it. Cleared when the next playback run begins.
  const userCollapsedRef = useRef(false);
  const hadPositionRef = useRef(false);
  const autoScrollPausedUntilRef = useRef(0);

  const hasMoreLines = lines.length > previewLines;

  // Lines are already chronological, but the sort makes the "last line that has
  // started" scan correct even for a provider that emits them out of order.
  const timeline = useMemo<LineTimeline>(
    () =>
      lines
        .map((line, index) => ({ index, start: line.start_seconds }))
        .filter(
          (entry): entry is { index: number; start: number } =>
            typeof entry.start === "number"
        )
        .sort((a, b) => a.start - b.start),
    [lines]
  );

  const lineWords = useMemo(() => lines.map(renderableWords), [lines]);

  const wordTimeline = useMemo<WordTimeline>(
    () =>
      lineWords
        .flatMap(
          (words, line) =>
            words?.map((word, index) => ({
              line,
              word: index,
              start: word.start_seconds
            })) ?? []
        )
        .sort((a, b) => a.start - b.start),
    [lineWords]
  );

  const syncable = !!playback && timeline.length > 0;

  useEffect(() => {
    if (!playback || timeline.length === 0) return;

    return playback.subscribe((time) => {
      if (time == null) hadPositionRef.current = false;
      else if (!hadPositionRef.current) {
        // A fresh run of the recording: the reader's earlier "Show less" no
        // longer applies, so the transcript may follow along again.
        hadPositionRef.current = true;
        userCollapsedRef.current = false;
      }

      // The player publishes at frame rate; re-render only when the line or
      // the word being spoken actually changes.
      setActiveIndex((current) => {
        const next = findActiveIndex(timeline, time);
        return next === current ? current : next;
      });
      setActiveWord((current) => {
        const next = findActiveWord(wordTimeline, time);
        return sameActiveWord(current, next) ? current : next;
      });
    });
  }, [playback, timeline, wordTimeline]);

  // Keep the spoken line readable: a collapsed transcript hides later lines
  // entirely and clamps the visible ones to a single truncated row.
  useEffect(() => {
    if (activeIndex == null || userCollapsedRef.current) return;

    setExpanded(true);
  }, [activeIndex]);

  useLayoutEffect(() => {
    if (expanded) return;
    const clipped = messageRefs.current.some(
      (el) => el != null && el.scrollWidth > el.clientWidth
    );
    setHasClippedLine(clipped);
  }, [expanded, lines, contactName, previewLines]);

  useLayoutEffect(() => {
    if (activeIndex == null) return;
    if (Date.now() < autoScrollPausedUntilRef.current) return;

    const pane = paneRef.current;
    const row = rowRefs.current[activeIndex];
    if (!pane || !row) return;

    // The pane is `relative`, so offsetTop is measured from it regardless of
    // the wrapper the rows sit in.
    const top = row.offsetTop - pane.clientHeight / 2 + row.offsetHeight / 2;
    pane.scrollTo({ top: Math.max(top, 0), behavior: "smooth" });
    // `expanded` matters: auto-expanding happens in a passive effect, so the
    // first active line is still unrendered when this runs for it. Re-running
    // once the list opens is what scrolls to that first line.
  }, [activeIndex, expanded]);

  // Stable across renders so the memoized rows do not re-render on every tick.
  const onSeek = useCallback(
    (seconds: number) => playback?.seek(seconds, { play: true }),
    [playback]
  );
  const registerRow = useCallback((index: number, el: HTMLElement | null) => {
    rowRefs.current[index] = el;
  }, []);
  const registerMessage = useCallback(
    (index: number, el: HTMLSpanElement | null) => {
      messageRefs.current[index] = el;
    },
    []
  );

  if (lines.length === 0) return null;

  const visibleLines = expanded ? lines : lines.slice(0, previewLines);
  const canToggle = hasMoreLines || hasClippedLine;
  // Bounding the height only once playback has a position keeps an idle
  // transcript laid out exactly as it always was.
  const isFollowing = syncable && activeIndex != null;

  const toggleExpanded = () => {
    if (expanded) userCollapsedRef.current = true;
    setExpanded(!expanded);
  };

  const pauseAutoScroll = () => {
    autoScrollPausedUntilRef.current = Date.now() + USER_SCROLL_PAUSE_MS;
  };

  let paneMaxHeight: number | undefined;
  if (isFollowing) paneMaxHeight = SYNC_PANE_MAX_HEIGHT_PX;
  else if (boxed) paneMaxHeight = BOXED_PANE_MAX_HEIGHT_PX;

  return (
    <>
      <div>
        <Show if={!hideHeader}>
          <p className="mb-1 text-xs font-medium text-content-200">
            Transcript
          </p>
        </Show>
        {/* One grid for every line so the speaker column is as wide as the longest
            name, never narrower than the old 100px, and never wider than 40% of
            the card: a contact known only by email used to overflow a fixed
            column and run into the message text. Past the cap the name is cut
            with an ellipsis (full value in the tooltip) and the colon stays. */}
        <div
          ref={paneRef}
          onWheel={pauseAutoScroll}
          onTouchMove={pauseAutoScroll}
          className={cx(
            "grid grid-cols-[fit-content(40%)_minmax(0,1fr)] text-sm text-content-1000",
            boxed
              ? "gap-x-6 gap-y-2 rounded-xl bg-alpha-20 px-4 py-3"
              : "gap-x-2 gap-y-1",
            (isFollowing || boxed) &&
              "relative overflow-y-auto scrollbar-thin scrollbar-thumb-alpha-100 scrollbar-track-transparent",
            // No scrollbar plugin is installed, so the scrollbar-* classes above
            // are inert; standard properties make the boxed pane's bar visible.
            boxed &&
              "[scrollbar-color:theme(colors.alpha.300)_transparent] [scrollbar-width:thin]"
          )}
          style={paneMaxHeight ? { maxHeight: paneMaxHeight } : undefined}
        >
          {visibleLines.map((line, i) => {
            const isAgent = line.speaker.toLowerCase() === "agent";
            return (
              <TranscriptRow
                key={i}
                index={i}
                speaker={isAgent ? "Agent" : contactName || line.speaker}
                isAgent={isAgent}
                message={line.message}
                words={lineWords[i]}
                start={line.start_seconds}
                isActive={i === activeIndex}
                activeWordIndex={
                  activeWord?.line === i ? activeWord.word : null
                }
                expanded={expanded}
                syncable={syncable}
                speakerClassName={speakerClassName}
                messageClassName={messageClassName}
                boxed={boxed}
                onSeek={onSeek}
                registerRow={registerRow}
                registerMessage={registerMessage}
              />
            );
          })}
        </div>
      </div>
      <Show if={canToggle}>
        <div>
          <button
            type="button"
            onClick={toggleExpanded}
            className={cx(
              "ml-auto flex items-center gap-1 text-xs font-medium transition-colors",
              toggleClassName
            )}
          >
            <span>{expanded ? "Show less" : "Show more"}</span>
            <HugeiconsIcon
              icon={expanded ? ArrowUp01Icon : ArrowDown01Icon}
              size={14}
            />
          </button>
        </div>
      </Show>
    </>
  );
};
